<script setup>
import {reactive, ref, computed, watch, onMounted, nextTick} from 'vue';
import { debounce } from 'lodash';
import MultiTextField from './multiTextField.vue';
import OptionsField from './optionsField.vue';
import SearchField from './searchField.vue';

const props = defineProps({
    filters: { type: Object, default: () => ({}) },
    disabled: { type: Boolean, default: false },
    searchCountUrl: { type: String, required: true },
    locationsUrl: { type: String, required: true },
    geocodeUrl: { type: String, required: true },
    previewUrl: { type: String, required: true },
    saveFormRequest: { type: Number, default: 0 },
});

const emit = defineEmits(['update:filters', 'preview', 'save']);

const searchFields = ref([
    {
        label: _mft('leadHunterCampaign:audience.jobTitles.title'),
        field: 'job_title',
        type: 'multiText',
        inputPlaceholder: _mft('lead-hunter:scout.filters.jobTitlesPlaceholder'),
    },
    {
        label: _mft('leadHunterCampaign:audience.companyIndustry.title'),
        field: 'company_industry',
        type: 'options',
        options: [
            { label: 'Accounting', value: 'accounting' },
            { label: 'Airlines/aviation', value: 'airlines/aviation' },
            { label: 'Automotive', value: 'automotive' },
            { label: 'Banking', value: 'banking' },
            { label: 'Biotechnology', value: 'biotechnology' },
            { label: 'Computer software', value: 'computer software' },
            { label: 'Construction', value: 'construction' },
            { label: 'Consumer goods', value: 'consumer goods' },
            { label: 'Design', value: 'design' },
            { label: 'E-learning', value: 'e-learning' },
            { label: 'Education management', value: 'education management' },
            { label: 'Entertainment', value: 'entertainment' },
            { label: 'Financial services', value: 'financial services' },
            { label: 'Food & beverages', value: 'food & beverages' },
            { label: 'Health, wellness and fitness', value: 'health, wellness and fitness' },
            { label: 'Hospital & health care', value: 'hospital & health care' },
            { label: 'Hospitality', value: 'hospitality' },
            { label: 'Human resources', value: 'human resources' },
            { label: 'Information technology and services', value: 'information technology and services' },
            { label: 'Insurance', value: 'insurance' },
            { label: 'Internet', value: 'internet' },
            { label: 'Legal services', value: 'legal services' },
            { label: 'Logistics and supply chain', value: 'logistics and supply chain' },
            { label: 'Management consulting', value: 'management consulting' },
            { label: 'Marketing and advertising', value: 'marketing and advertising' },
            { label: 'Media production', value: 'media production' },
            { label: 'Non-profit organization management', value: 'non-profit organization management' },
            { label: 'Oil & energy', value: 'oil & energy' },
            { label: 'Pharmaceuticals', value: 'pharmaceuticals' },
            { label: 'Professional training & coaching', value: 'professional training & coaching' },
            { label: 'Real estate', value: 'real estate' },
            { label: 'Retail', value: 'retail' },
            { label: 'Staffing and recruiting', value: 'staffing and recruiting' },
            { label: 'Telecommunications', value: 'telecommunications' },
            { label: 'Transportation/trucking/railroad', value: 'transportation/trucking/railroad' },
        ],
        inputPlaceholder: _mft('lead-hunter:scout.filters.companyIndustryPlaceholder'),
    },
    {
        label: _mft('leadHunterCampaign:audience.companySize.title'),
        field: 'company_size',
        type: 'options',
        options: [
            { label: '1-10', value: '1-10' },
            { label: '11-50', value: '11-50' },
            { label: '51-200', value: '51-200' },
            { label: '201-500', value: '201-500' },
            { label: '501-1000', value: '501-1000' },
            { label: '1001-5000', value: '1001-5000' },
            { label: '5001-10000', value: '5001-10000' },
            { label: '10001+', value: '10001+' },
        ],
        inputPlaceholder: _mft('shared:search')+'...',
    },
    {
        label: _mft('leadHunterCampaign:audience.locations.title'),
        field: 'location',
        type: 'geocode',
        inputPlaceholder: _mft('lead-hunter:scout.filters.locationPlaceholder'),
    },
]);

const parameters = reactive({
    job_title: [],
    company_industry: [],
    company_size: [],
    location: [],
});

const leadCount = ref(null);
const counting = ref(false);
const previewing = ref(false);
const showSaveForm = ref(false);
const searchName = ref('');
const budget = ref(null);
const initialCollectCount = ref(10);
const lastAppliedFiltersSnapshot = ref('');
const lastEmittedFiltersSnapshot = ref('');

const loadBudget = async () => {
    try {
        const response = await fetch(route('lead-searches.budget'));
        budget.value = await response.json();
        if (budget.value.max_leads > 0) {
            initialCollectCount.value = budget.value.default_leads || Math.min(10, budget.value.max_leads);
        }
    } catch (e) {
        console.error('Failed to load budget', e);
    }
};

const saveUsagePercent = computed(() => {
    if (!budget.value) return 0;
    return Math.round((initialCollectCount.value * budget.value.cost_per_lead / budget.value.monthly_limit) * 100);
});
const locationInput = ref('');
const geocoding = ref(false);
const locationRadius = ref('10mi');

const radiusOptions = [
    { label: '5 mi', value: '5mi' },
    { label: '10 mi', value: '10mi' },
    { label: '25 mi', value: '25mi' },
    { label: '50 mi', value: '50mi' },
    { label: '100 mi', value: '100mi' },
];

const geocodeLocation = debounce(async () => {
    if (props.disabled) return;

    const query = locationInput.value.trim();
    if (query.length < 2 || geocoding.value) return;

    geocoding.value = true;
    try {
        const response = await fetch(`${props.geocodeUrl}?query=${encodeURIComponent(query)}`);
        const data = await response.json();

        if (data.results && data.results.length > 0) {
            const result = data.results[0];
            const locationObj = {
                label: query,
                value: query,
                lat: result.lat,
                lng: result.lng,
                radius: locationRadius.value,
            };
            if (!parameters.location.some(o => o.label === query)) {
                parameters.location.push(locationObj);
            }
            locationInput.value = '';
        }
    } catch (e) {
        console.error('Geocode failed', e);
    } finally {
        geocoding.value = false;
    }
}, 300);

const removeLocation = (location) => {
    if (props.disabled) return;

    parameters.location = parameters.location.filter(o => o.value !== location.value);
};

onMounted(() => {
    syncFromProp();
    loadBudget();
});

let syncing = false;

const normalizeForSnapshot = (value) => {
    if (Array.isArray(value)) {
        return value.map(normalizeForSnapshot);
    }

    if (value && typeof value === 'object') {
        return Object.keys(value)
            .sort()
            .reduce((normalized, key) => {
                normalized[key] = normalizeForSnapshot(value[key]);
                return normalized;
            }, {});
    }

    return value;
};

const getFiltersSnapshot = (filters) => JSON.stringify(normalizeForSnapshot(filters));

const resetAppliedFiltersSnapshot = () => {
    lastAppliedFiltersSnapshot.value = getFiltersSnapshot(getCleanFilters());
};

/**
 * Normalize filter keys from Scout AI (which may return "Job Title" instead of "job_title")
 * to match our internal parameter keys.
 */
const normalizeFilterKey = (key) => {
    const normalized = key.toLowerCase().replace(/\s+/g, '_');
    const aliases = {
        'job_title': 'job_title',
        'job_titles': 'job_title',
        'industry': 'company_industry',
        'company_industry': 'company_industry',
        'company_size': 'company_size',
        'size': 'company_size',
        'location': 'location',
        'locations': 'location',
    };
    return aliases[normalized] || null;
};

const syncFromProp = () => {
    syncing = true;
    let locationSync = Promise.resolve();

    // Build a normalized map from incoming filters
    const normalizedIncoming = {};
    for (const [key, value] of Object.entries(props.filters || {})) {
        const normKey = normalizeFilterKey(key);
        if (normKey) {
            // Normalize values: ensure it's an array, then map to {label, value} objects
            let values = Array.isArray(value) ? value : [value];
            normalizedIncoming[normKey] = values.map(v =>
                typeof v === 'string' ? { label: v, value: v } : v
            );
        }
    }

    for (const key of ['job_title', 'company_industry', 'company_size']) {
        const incoming = normalizedIncoming[key];
        if (incoming && incoming.length > 0) {
            const currentValues = parameters[key].map(o => o.value).sort().join(',');
            const newValues = incoming.map(o => o.value).sort().join(',');
            if (currentValues !== newValues) {
                parameters[key].splice(0, parameters[key].length, ...incoming);
            }
        } else if (!incoming) {
            if (parameters[key].length > 0) {
                parameters[key].splice(0, parameters[key].length);
            }
        }
    }

    // Handle locations — geocode string locations from Scout AI
    const incomingLocations = normalizedIncoming['location'];
    if (incomingLocations && incomingLocations.length > 0) {
        const locationsWithCoordinates = incomingLocations
            .filter(loc => typeof loc.lat !== 'undefined')
            .map(loc => ({
                ...loc,
                value: loc.value || loc.label,
                radius: loc.radius || '10mi',
            }));
        const locationsToGeocode = incomingLocations.filter(loc => typeof loc.lat === 'undefined');

        if (locationsToGeocode.length > 0) {
            // Geocode each location asynchronously
            locationSync = Promise.all(locationsToGeocode.map(async (loc) => {
                try {
                    const response = await fetch(`${props.geocodeUrl}?query=${encodeURIComponent(loc.label)}`);
                    const data = await response.json();
                    if (data.results && data.results.length > 0) {
                        return {
                            label: loc.label,
                            value: loc.label,
                            lat: data.results[0].lat,
                            lng: data.results[0].lng,
                            radius: '10mi',
                        };
                    }
                } catch (e) { /* skip */ }
                return null;
            })).then(results => {
                const valid = results.filter(r => r !== null);
                parameters.location.splice(
                    0,
                    parameters.location.length,
                    ...locationsWithCoordinates,
                    ...valid
                );
            });
        } else {
            // Already have lat/lng — just sync
            if (getFiltersSnapshot(parameters.location) !== getFiltersSnapshot(incomingLocations)) {
                parameters.location.splice(0, parameters.location.length, ...incomingLocations);
            }
        }
    } else if (!incomingLocations) {
        if (parameters.location.length > 0) {
            parameters.location.splice(0, parameters.location.length);
        }
    }

    locationSync.finally(() => {
        nextTick(() => {
            resetAppliedFiltersSnapshot();
            syncing = false;
        });
    });
};

const addFieldOption = (field, option) => {
    if (props.disabled) return;

    if (!parameters[field.field].some(o => o.value === option.value)) {
        parameters[field.field].push(option);
    }
};

const removeFieldOption = (field, option) => {
    if (props.disabled) return;

    parameters[field.field] = parameters[field.field].filter(o => o.value !== option.value);
};

const hasAnyFilter = () => {
    return Object.values(parameters).some(v => v.length > 0);
};

const getCleanFilters = () => {
    const clean = {};
    for (const [key, values] of Object.entries(parameters)) {
        if (values.length > 0) {
            if (key === 'location') {
                // Preserve lat/lng/radius for geocoded locations
                clean[key] = values.map(v => ({
                    label: v.label || v.value,
                    lat: v.lat,
                    lng: v.lng,
                    radius: v.radius || locationRadius.value,
                }));
            } else {
                clean[key] = values.map(v => typeof v === 'object' ? (v.value || v.label) : v);
            }
        }
    }
    return clean;
};

const filtersHaveChanged = computed(() => {
    if (props.disabled) {
        return false;
    }

    if (!hasAnyFilter()) {
        return false;
    }

    return getFiltersSnapshot(getCleanFilters()) !== lastAppliedFiltersSnapshot.value;
});

// Count is shown in Scout's chat response — no separate API call needed

watch(() => props.filters, (filters) => {
    if (getFiltersSnapshot(filters || {}) === lastEmittedFiltersSnapshot.value) {
        return;
    }

    syncFromProp();
}, { deep: true });

watch(() => props.saveFormRequest, () => {
    if (!props.disabled) {
        showSaveForm.value = true;
    }
});

watch(parameters, () => {
    if (syncing || props.disabled) return;

    const filters = getCleanFilters();
    lastEmittedFiltersSnapshot.value = getFiltersSnapshot(filters);
    emit('update:filters', filters);
}, { deep: true });

watch(locationRadius, (radius) => {
    if (syncing || props.disabled || parameters.location.length === 0) return;

    parameters.location.splice(
        0,
        parameters.location.length,
        ...parameters.location.map(location => ({
            ...location,
            radius,
        }))
    );
});

const handlePreview = async () => {
    if (props.disabled || !hasAnyFilter() || previewing.value) return;

    previewing.value = true;
    try {
        const response = await fetch(props.previewUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content,
            },
            body: JSON.stringify({ filters: getCleanFilters() }),
        });
        const data = await response.json();
        emit('preview', {
            total: data.total,
            profiles: data.profiles,
        });
        resetAppliedFiltersSnapshot();
    } catch (e) {
        console.error('Failed to preview', e);
    } finally {
        previewing.value = false;
    }
};

const handleSave = () => {
    if (props.disabled || !searchName.value.trim()) return;
    emit('save', {
        name: searchName.value.trim(),
        filters: getCleanFilters(),
        collectCount: initialCollectCount.value,
    });
    showSaveForm.value = false;
    searchName.value = '';
};

const formatNumber = (num) => {
    if (num === null || num === undefined) return '—';
    return new Intl.NumberFormat().format(num);
};
</script>

<template>
    <div class="h-full flex flex-col">
        <div class="px-3 py-2.5 border-b border-gray-200">
            <div class="text-xs font-semibold text-gray-900">
                <mf-translate domainkey="lead-hunter:scout.filters"></mf-translate>
            </div>
            <div class="text-xxs text-gray-500">
                <mf-translate domainkey="lead-hunter:scout.filtersSubtitle"></mf-translate>
            </div>
        </div>

        <div v-if="disabled" class="px-3 pt-3 pb-1">
            <div class="rounded-md bg-amber-50 border border-amber-200 px-3 py-2">
                <p class="text-xxs text-amber-800 mb-0">
                    <i class="fa-regular fa-lock mr-1"></i>
                    {{ _mft('lead-hunter:scout.savedSearchReadonly') }}
                </p>
            </div>
        </div>

        <div v-else-if="!hasAnyFilter()" class="px-3 pt-3 pb-1">
            <div class="rounded-md bg-aiAgentLeadFinder/10 border border-aiAgentLeadFinder/20 px-3 py-2">
                <p class="text-xxs text-gray-600 mb-0">
                    <i class="fa-regular fa-sparkles text-aiAgentLeadFinder mr-1"></i>
                    {{ _mft('lead-hunter:scout.filtersHint') }}
                </p>
            </div>
        </div>

        <div class="flex-1 overflow-y-auto px-3 py-2 space-y-3">
            <div v-for="field in searchFields" :key="field.field">
                <input-label class="block mb-1" :label="field.label"></input-label>

                <multi-text-field
                    v-if="field.type === 'multiText'"
                    :field="field"
                    :parameters="parameters"
                    :placeholder="field.inputPlaceholder"
                    :disabled="disabled"
                    @add-field-option="addFieldOption"
                    @remove-field-option="removeFieldOption"
                />

                <options-field
                    v-if="field.type === 'options'"
                    :field="field"
                    :parameters="parameters"
                    :placeholder="field.inputPlaceholder"
                    :disabled="disabled"
                    @add-field-option="addFieldOption"
                    @remove-field-option="removeFieldOption"
                />

                <search-field
                    v-if="field.type === 'search'"
                    :field="field"
                    :parameters="parameters"
                    :search-url="field.searchUrl"
                    :placeholder="field.inputPlaceholder"
                    :disabled="disabled"
                    @add-field-option="addFieldOption"
                    @remove-field-option="removeFieldOption"
                />

                <!-- Geocode location field -->
                <div v-if="field.type === 'geocode'">
                    <div class="relative">
                        <input-text
                            :placeholder="field.inputPlaceholder"
                            v-model:content="locationInput"
                            @keyup.enter="geocodeLocation()"
                            :disabled="disabled"
                            auto-complete="off"
                            data-1p-ignore
                            class="!mb-0"
                        ></input-text>
                        <div class="absolute right-2 top-2.5">
                            <button-primary
                                v-if="locationInput.length > 1"
                                @click="geocodeLocation()"
                                :disabled="geocoding || disabled"
                                size="sm"
                            >
                                <mf-spinner-medium v-if="geocoding" class="w-6 h-3" />
                                <template v-else>{{ _mft('shared:add') }}</template>
                            </button-primary>
                        </div>
                    </div>
                    <!-- Radius selector -->
                    <div class="flex items-center space-x-2 mt-1 mb-2">
                        <input-label label="Radius"></input-label>
                        <div class="flex items-center gap-1.5">
                            <select
                                v-model="locationRadius"
                                :disabled="disabled"
                                class="text-[10px] border-gray-200 rounded px-1 py-0.5 bg-white"
                                :class="disabled ? 'text-gray-400 bg-gray-50 cursor-not-allowed' : ''"
                            >
                                <option v-for="opt in radiusOptions" :key="opt.value" :value="opt.value">
                                    {{ opt.label }}
                                </option>
                            </select>
                        </div>
                    </div>
                    <!-- Selected locations -->
                    <ul
                        v-if="parameters.location.length > 0"
                        class="mt-1 flex flex-wrap items-center px-0.5"
                    >
                        <li
                            v-for="(loc, i) in parameters.location"
                            :key="i"
                            class="mr-1 mb-1 last:mr-0"
                        >
                            <pill>
                                <div class="flex space-x-1">
                                    <div>{{ loc.label }}</div>
                                    <button
                                        v-if="!disabled"
                                        @click="removeLocation(loc)"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                                            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                                        </svg>
                                    </button>
                                </div>
                            </pill>
                        </li>
                    </ul>
                </div>
            </div>
        </div>

        <div v-if="filtersHaveChanged" class="px-4 py-2.5 border-t border-gray-200">
            <button-primary
                @click="handlePreview"
                :disabled="previewing"
                class="w-full justify-center"
            >
                <mf-spinner-medium v-if="previewing" class="w-6 h-3" />
                <template v-else>
                    <i class="fa-regular fa-arrows-rotate mr-1.5"></i>
                    {{ _mft('lead-hunter:scout.updateSearch') }}
                </template>
            </button-primary>
        </div>

        <Teleport to="body">
            <div
                v-if="!disabled && showSaveForm"
                class="fixed inset-0 z-[9998] flex items-center justify-center p-4"
                role="dialog"
                aria-modal="true"
                aria-labelledby="save-search-title"
            >
                <div
                    class="absolute inset-0 bg-black/50 backdrop-blur-sm"
                    @click="showSaveForm = false"
                ></div>

                <div class="relative w-full max-w-md overflow-hidden rounded-xl bg-white shadow-2xl">
                    <div class="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                        <h2 id="save-search-title" class="text-base font-semibold text-gray-900">
                            {{ _mft('lead-hunter:scout.saveSearchTitle') }}
                        </h2>
                        <button
                            type="button"
                            class="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                            @click="showSaveForm = false"
                            :aria-label="_mft('shared:action.cancel')"
                        >
                            <i class="fa-light fa-xmark text-lg"></i>
                        </button>
                    </div>

                    <div class="space-y-4 px-5 py-4">
                        <input-text
                            v-model:content="searchName"
                            :label="_mft('leadHunter:campaign.name')"
                            :placeholder="_mft('lead-hunter:scout.searchNamePlaceholder')"
                            maxlength="255"
                        ></input-text>

                        <div v-if="budget && budget.max_leads > 0">
                            <input-label class="block mb-2" :label="_mft('lead-hunter:scout.howManyLeads')">
                            </input-label>
                            <div class="flex items-center gap-3">
                                <input
                                    type="range"
                                    v-model.number="initialCollectCount"
                                    :min="1"
                                    :max="budget.max_leads"
                                    class="flex-1 h-1.5 rounded-full appearance-none bg-gray-200 accent-brandPrimary"
                                />
                                <span class="w-10 text-right text-sm font-semibold text-gray-900">{{ initialCollectCount }}</span>
                            </div>
                            <p class="mt-2 mb-0 text-xs text-gray-400">
                                {{ saveUsagePercent }}% {{ _mft('lead-hunter:scout.ofBudget') }}<br>
                                {{ _mft('lead-hunter:scout.batchSizeWarning') }}
                            </p>
                        </div>
                        <div v-else-if="budget && budget.max_leads === 0" class="rounded-md border border-red-100 bg-red-50 px-3 py-2 text-xs font-bold text-red-600">
                            <i class="fa-regular fa-lock mr-1"></i>
                            {{ _mft('lead-hunter:scout.noBudget') }}
                        </div>
                    </div>

                    <div class="flex justify-end gap-2 border-t border-gray-100 px-5 py-4">
                        <button-secondary @click="showSaveForm = false">
                            {{ _mft('shared:action.cancel') }}
                        </button-secondary>
                        <button-success
                            @click="handleSave"
                            :disabled="!searchName.trim() || !budget || budget.max_leads === 0"
                        >
                            {{ _mft('lead-hunter:scout.saveAndCollect') }}
                        </button-success>
                    </div>
                </div>
            </div>
        </Teleport>
    </div>
</template>
